import { Button, Card, CardContent, Input } from '@/components/ui';
import {
  useProductBrands,
  useProductCategories,
  useProducts,
  useProductStats,
} from '@/hooks/use-products';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_SUPPLIERS, PRODUCT_STATUSES } from './mock-data';
import type { ProductStatus } from './types';

export function ProductListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProductStatus | 'all'>('all');
  // Q-T156: a szűrő a valós `ProductCategory.code`-okkal dolgozik (`CAT-KISGEP`…),
  // mert a `ProductDTO.category` is azt hordozza. Korábban a mock `power_tool`-értékek
  // ellen hasonlított, így a szűrő SOHA nem adott találatot.
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const { categories } = useProductCategories();
  const [supplierFilter, setSupplierFilter] = useState<string | 'all'>('all');
  const [brandFilter, setBrandFilter] = useState<string | 'all'>('all');
  const [lowStockOnly, setLowStockOnly] = useState(false);

  // Build API filters
  const apiFilters = useMemo(() => {
    const filters: {
      status?: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
      search?: string;
      brand?: string;
      lowStock?: boolean;
      pageSize?: number;
      // Interim ceiling (BE-max=100, Javo-ratify): a client-side category+supplier filter
      // a teljes halmazon kell működjön → load-all-at-100 (data-loss 20→100). Full server-mode
      // a BE category/supplier-filter-support után (ADR-followup).
    } = { pageSize: 100 };
    if (statusFilter !== 'all') {
      const statusMap: Record<string, 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED'> = {
        active: 'ACTIVE',
        inactive: 'INACTIVE',
        discontinued: 'DISCONTINUED',
      };
      filters.status = statusMap[statusFilter];
    }
    if (searchTerm) {
      filters.search = searchTerm;
    }
    if (brandFilter !== 'all') {
      filters.brand = brandFilter;
    }
    if (lowStockOnly) {
      filters.lowStock = true;
    }
    return filters;
  }, [statusFilter, searchTerm, brandFilter, lowStockOnly]);

  // A page jelenleg nem renderel loading/error state-et → a destrukturált isLoading/error
  // unused-var lint-error volt (a list-loading/error UI külön follow-up, ha kell).
  const { products } = useProducts(apiFilters);
  const { stats } = useProductStats();
  const { brands } = useProductBrands();

  // Client-side category and supplier filters (since API doesn't support all of them)
  const filteredProducts = useMemo(() => {
    let result = products;
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    if (supplierFilter !== 'all') {
      result = result.filter(p => p.supplier?.id === supplierFilter);
    }
    return result;
  }, [products, categoryFilter, supplierFilter]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getStatusBadge = (status: ProductStatus) => {
    const config = PRODUCT_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config?.label ?? status}
      </span>
    );
  };

  const getCategoryBadge = (category: string) => {
    // A DTO `category` mezője a `ProductCategory.code` — a beszédes nevet
    // az élő kategória-listából oldjuk fel (fallback: maga a kód).
    const match = categories.find(c => c.code === category);
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-slate-700 dark:text-gray-200">
        {match?.name ?? category}
      </span>
    );
  };

  const getStockBadge = (product: { stockQuantity: number; minStockLevel: number }) => {
    if (product.stockQuantity === 0) {
      return (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {t('product.stockBadge.none')}
        </span>
      );
    }
    if (product.stockQuantity < product.minStockLevel) {
      return (
        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
          {t('product.stockBadge.low')}
        </span>
      );
    }
    return (
      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
        {t('product.stockBadge.ok')}
      </span>
    );
  };

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              ← {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('product.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('product.subtitle')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/products/new')}
            className="bg-primary hover:bg-primary/90"
          >
            + {t('product.newProduct')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {stats?.total ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('product.stats.total')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats?.active ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('product.stats.active')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {stats?.lowStock ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('product.stats.lowStock')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {stats?.outOfStock ?? 0}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('product.stats.outOfStock')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatPrice(stats?.totalValue ?? 0)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('product.stats.stockValue')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[250px] flex-1">
                <Input
                  placeholder={t('product.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('product.allCategories')}</option>
                {categories.map(c => (
                  <option key={c.id} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as ProductStatus | 'all')}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('product.allStatuses')}</option>
                {PRODUCT_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <select
                value={supplierFilter}
                onChange={e => setSupplierFilter(e.target.value)}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('product.allSuppliers')}</option>
                {MOCK_SUPPLIERS.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <select
                value={brandFilter}
                onChange={e => setBrandFilter(e.target.value)}
                className="rounded-md border px-3 py-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">{t('product.filter.allBrands')}</option>
                {brands.map(b => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={lowStockOnly}
                  onChange={e => setLowStockOnly(e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                {t('product.lowStockOnly')}
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Products table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.product')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.category')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.barcode')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.supplier')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.netPrice')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.grossPrice')}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.stock')}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.tableHeaders.status')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"></th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-slate-700">
                  {filteredProducts.map(product => (
                    <tr
                      key={product.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {product.thumbnailUrl ? (
                            <div className="h-10 w-10 flex-shrink-0 rounded bg-gray-100 dark:bg-slate-700">
                              <img
                                src={product.thumbnailUrl}
                                alt={product.name}
                                className="h-full w-full object-cover rounded"
                              />
                            </div>
                          ) : (
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-gray-300">
                              📦
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {product.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {product.sku}
                              {product.brand && ` • ${product.brand}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getCategoryBadge(product.category)}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300 font-mono">
                          {product.barcode ?? '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {product.supplier?.name ?? '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {formatPrice(product.sellingPriceNet)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="text-gray-600 dark:text-gray-300">
                          {formatPrice(product.sellingPriceGross)}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {product.availableQuantity} / {product.stockQuantity}
                          </span>
                          <div className="flex items-center gap-1">
                            {getStockBadge(product)}
                            {product.reservedQuantity > 0 && (
                              <span className="rounded-full border border-orange-500 px-2 py-0.5 text-xs font-medium text-orange-600 dark:text-orange-400">
                                {t('inventory.reservedForWorksheet')}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(product.status)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/products/${product.id}`);
                          }}
                        >
                          {t('product.detailsButton')} →
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredProducts.length === 0 && (
                <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                  <p>{t('product.noResults')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('product.displayedCount', { count: String(filteredProducts.length) })}
        </p>
      </main>
    </div>
  );
}
